import { getProductDetailsDirect } from '../clients/product_client';
import { logError, logInfo } from '../config_and_helpers/logger';
import {
  getProductCatalogNumber,
  getProductImageUrl,
  getProductSupplierId,
} from './productDisplay';

const LOG_CONTEXT = 'orderRequestCart';

/**
 * Build catalog quote-request lines from normalized order request lines.
 * Used for PATCH payloads and `POST quotes/request_from_catalog/`.
 *
 * @param {Array<Object>} lines - Order request lines with product_id and quantity.
 * @returns {Array<{ product_id: number, quantity: number }>}
 */
export function buildCatalogQuoteLinesFromOrderRequestLines(lines) {
  return (Array.isArray(lines) ? lines : [])
    .map(line => ({
      product_id: Number(line?.product_id),
      quantity: Number(line?.quantity),
    }))
    .filter(
      line =>
        Number.isInteger(line.product_id) &&
        line.product_id > 0 &&
        Number.isInteger(line.quantity) &&
        line.quantity > 0
    );
}

/**
 * Group normalized order request lines by supplier for quote-request UI.
 *
 * @param {Array<Object>} lines - Order request lines with supplier_id / supplier_name.
 * @returns {Array<{ supplierKey: string, supplierId: number|null, supplierName: string, lines: Object[] }>}
 */
export function groupOrderRequestLinesBySupplier(lines) {
  const map = new Map();

  (Array.isArray(lines) ? lines : []).forEach(line => {
    const supplierId =
      line?.supplier_id != null && !Number.isNaN(Number(line.supplier_id))
        ? Number(line.supplier_id)
        : null;
    const supplierKey =
      supplierId != null && Number.isInteger(supplierId) && supplierId > 0
        ? String(supplierId)
        : 'unknown';
    const supplierName =
      line?.supplier_name?.trim() ||
      (supplierId != null ? `Supplier #${supplierId}` : 'Unknown supplier');

    if (!map.has(supplierKey)) {
      map.set(supplierKey, {
        supplierKey,
        supplierId,
        supplierName,
        lines: [],
      });
    }
    map.get(supplierKey).lines.push(line);
  });

  return [...map.values()].sort((a, b) =>
    a.supplierName.localeCompare(b.supplierName)
  );
}

/**
 * Resolve supplier object for a cart row from order request line and optional product detail.
 *
 * @param {Object} line
 * @param {Object|null} productDetail
 * @returns {{ id: number|string, name: string }|null}
 */
function resolveSupplierForCartLine(line, productDetail) {
  if (line?.supplier_id != null) {
    return {
      id: line.supplier_id,
      name: line.supplier_name || `Supplier #${line.supplier_id}`,
    };
  }
  const supplierId = getProductSupplierId(productDetail);
  if (supplierId != null) {
    const s = productDetail?.supplier;
    const name =
      line?.supplier_name ||
      (typeof s === 'object' && s?.name) ||
      productDetail?.supplier_name ||
      `Supplier #${supplierId}`;
    return { id: supplierId, name };
  }
  return null;
}

/**
 * Map one order request line to a Shop cart row (same shape as ShopModal / ItemCard).
 *
 * @param {Object} line - Normalized order request line.
 * @param {Object|null} [productDetail] - Optional product detail for image/supplier fallback.
 * @returns {{ item: Object|null, skipReason: string|null }}
 */
export function buildCartItemFromOrderRequestLine(
  line,
  productDetail = null,
  {
    orderRequestId = null,
    orderRequestContactName = '',
    orderRequestContactEmail = '',
  } = {}
) {
  const productId = line?.product_id;
  const qty = Number(line?.quantity);

  if (productId == null) {
    return { item: null, skipReason: 'Missing product id' };
  }
  if (!Number.isInteger(qty) || qty < 1) {
    return { item: null, skipReason: 'Invalid quantity' };
  }

  const supplier = resolveSupplierForCartLine(line, productDetail);
  if (!supplier?.id) {
    return { item: null, skipReason: 'Missing supplier' };
  }

  const catNum =
    line?.cat_num ||
    getProductCatalogNumber(productDetail) ||
    String(productId);

  const name =
    line?.product_name ||
    productDetail?.name ||
    `Product #${productId}`;

  return {
    item: {
      product: productId,
      cat_num: catNum,
      name,
      image_url: getProductImageUrl(productDetail, null),
      supplier,
      quantity: qty,
      order_request_id: orderRequestId,
      order_request_contact_name: orderRequestContactName || '',
      order_request_contact_email: orderRequestContactEmail || '',
    },
    skipReason: null,
  };
}

/**
 * Merge incoming cart rows into existing cart (dedupe by cat_num, add quantities).
 *
 * @param {Array<Object>} existingCart
 * @param {Array<Object>} incomingItems
 * @returns {{ cart: Array<Object>, addedCount: number, mergedCount: number }}
 */
export function mergeCartItems(existingCart, incomingItems) {
  const cart = Array.isArray(existingCart) ? [...existingCart] : [];
  let addedCount = 0;
  let mergedCount = 0;

  (Array.isArray(incomingItems) ? incomingItems : []).forEach(incoming => {
    const catKey = incoming?.cat_num;
    const idx = cart.findIndex(row => row.cat_num === catKey);
    if (idx >= 0) {
      cart[idx] = {
        ...cart[idx],
        quantity: Number(cart[idx].quantity) + Number(incoming.quantity),
      };
      mergedCount += 1;
    } else {
      cart.push(incoming);
      addedCount += 1;
    }
  });

  return { cart, addedCount, mergedCount };
}

/**
 * Fetch product details for unique ids (best-effort images/supplier fallback).
 *
 * @param {string} token
 * @param {Array<number|string>} productIds
 * @returns {Promise<Map<string, Object>>}
 */
async function fetchProductDetailsMap(token, productIds) {
  const map = new Map();
  const uniqueIds = [...new Set(productIds.filter(id => id != null))];

  await Promise.all(
    uniqueIds.map(async productId => {
      const detail = await getProductDetailsDirect(token, productId);
      if (detail) {
        map.set(String(productId), detail);
      }
    })
  );

  return map;
}

/**
 * Add all order request lines to the client-side Shop cart (procurement staging).
 * Does not call backend; quote validation happens at cart checkout.
 *
 * @async
 * @param {Object} params
 * @param {string} params.token
 * @param {Array<Object>} params.lines
 * @param {Array<Object>} params.cart
 * @param {function} params.setCart
 * @returns {Promise<{ success: boolean, addedCount: number, mergedCount: number, skipped: Array<{ line: Object, reason: string }> }>}
 */
export async function addOrderRequestLinesToCart({
  token,
  lines,
  cart,
  setCart,
  orderRequestId = null,
  orderRequestContactName = '',
  orderRequestContactEmail = '',
}) {
  const lineList = Array.isArray(lines) ? lines : [];
  const skipped = [];
  const incomingItems = [];

  logInfo(LOG_CONTEXT, 'addOrderRequestLinesToCart start', {
    lineCount: lineList.length,
  });

  try {
    const productIds = lineList.map(l => l?.product_id).filter(Boolean);
    const productMap = token
      ? await fetchProductDetailsMap(token, productIds)
      : new Map();

    lineList.forEach(line => {
      const detail = productMap.get(String(line?.product_id)) ?? null;
      const { item, skipReason } = buildCartItemFromOrderRequestLine(
        line,
        detail,
        {
          orderRequestId,
          orderRequestContactName,
          orderRequestContactEmail,
        }
      );
      if (item) {
        incomingItems.push(item);
      } else {
        skipped.push({ line, reason: skipReason || 'Could not add line' });
      }
    });

    if (incomingItems.length === 0) {
      logInfo(LOG_CONTEXT, 'addOrderRequestLinesToCart no items added', {
        skippedCount: skipped.length,
      });
      return {
        success: false,
        addedCount: 0,
        mergedCount: 0,
        skipped,
      };
    }

    const { cart: nextCart, addedCount, mergedCount } = mergeCartItems(
      cart,
      incomingItems
    );
    setCart(nextCart);

    logInfo(LOG_CONTEXT, 'addOrderRequestLinesToCart success', {
      addedCount,
      mergedCount,
      skippedCount: skipped.length,
      cartSize: nextCart.length,
    });

    return {
      success: true,
      addedCount,
      mergedCount,
      skipped,
    };
  } catch (error) {
    logError(LOG_CONTEXT, 'addOrderRequestLinesToCart failed', { error });
    return {
      success: false,
      addedCount: 0,
      mergedCount: 0,
      skipped,
      error,
    };
  }
}

/**
 * Filter order request lines that belong to a supplier inbox group.
 *
 * @param {Array<Object>} lines
 * @param {number|string|null} supplierId - Accordion group supplier id.
 * @returns {Array<Object>}
 */
export function filterOrderRequestLinesForSupplier(lines, supplierId) {
  const list = Array.isArray(lines) ? lines : [];
  if (supplierId == null || supplierId === '') {
    return list;
  }
  const key = String(supplierId);
  return list.filter(line => {
    if (line?.supplier_id != null && line.supplier_id !== '') {
      return String(line.supplier_id) === key;
    }
    return true;
  });
}

/**
 * Build per-request catalog quote jobs for a supplier accordion group on the inbox list.
 *
 * @param {{ supplierId?: *, requests?: Array<Object> }} group
 * @returns {Array<{ orderRequestId: number, requestRef: string, lines: Array<{ product_id: number, quantity: number }> }>}
 */
/**
 * Whether a supplier inbox group has at least one open order request.
 *
 * @param {{ requests?: Array<Object> }} group
 * @returns {boolean}
 */
export function supplierGroupHasRequestedOrders(group) {
  return (Array.isArray(group?.requests) ? group.requests : []).some(
    request => request?.status === 'REQUESTED'
  );
}

/**
 * @param {{ requests?: Array<Object> }} group
 * @returns {Array<Object>}
 */
export function getRequestedOrdersInSupplierGroup(group) {
  return (Array.isArray(group?.requests) ? group.requests : []).filter(
    request => request?.status === 'REQUESTED'
  );
}

export function getQuotableQuoteJobsForSupplierGroup(group) {
  const jobs = [];
  (Array.isArray(group?.requests) ? group.requests : []).forEach(request => {
    if (request?.status !== 'REQUESTED') return;
    const filtered = filterOrderRequestLinesForSupplier(
      request.lines,
      group.supplierId
    );
    const lines = buildCatalogQuoteLinesFromOrderRequestLines(filtered);
    if (lines.length === 0) return;
    jobs.push({
      orderRequestId: Number(request.id),
      requestRef: request.request_ref ?? '',
      lines,
    });
  });
  return jobs;
}

/**
 * Build user-facing toast message for add-to-cart result.
 *
 * @param {{ success: boolean, addedCount: number, mergedCount: number, skipped: Array }} result
 * @returns {string}
 */
export function formatAddToCartToastMessage(result) {
  if (!result?.success) {
    const firstSkip = result?.skipped?.[0]?.reason;
    return firstSkip
      ? `No items added to cart: ${firstSkip}`
      : 'No items could be added to cart';
  }

  const parts = [];
  const total = (result.addedCount || 0) + (result.mergedCount || 0);
  parts.push(`Added ${total} item(s) to cart`);
  if (result.mergedCount > 0) {
    parts.push(`${result.mergedCount} merged with existing cart lines`);
  }
  if (result.skipped?.length > 0) {
    parts.push(`${result.skipped.length} skipped`);
  }
  return parts.join('; ');
}
